import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Layout,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
} from "antd";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import {
  formatNumber,
  formatDate,
  formatString,
  getUserBranch,
  getUserRole,
  handleChangePage,
  handleExportToExcel,
  handleExportToPDF,
  handlePageSizeChange,
} from "../../Utils/Common";
import CustomTable from "../../Components/Table/CustomTable";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import ErrorModal from "../../Components/CustomModals/ErrorModal";
import SuccessModal from "../../Components/CustomModals/SuccessModal";

const { Option } = Select;

const BorrowedCash = () => {
  const [loading, setLoading] = useState("");
  const [showModal, setShowModal] = useState("");
  const [filterConfig, setFilterConfig]: any = useState({ page: 1 });
  const [dataSize, setDataSize] = useState(0);
  const [form] = Form.useForm();
  const [selectedRow, setSelectedRow]: any = useState({});
  const [dataSource, setDataSource]: any = useState([]);
  const [branches, setBranches]: any = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  function clearFields() {
    setSelectedRow({});
    form.resetFields();
  }

  function handleFilterChange(value: any, key: string) {
    setFilterConfig((prev: any) => ({ ...prev, [key]: value, page: 1 }));
  }

  const columns = [
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: 'right' as any,
      render: (text: number) => <span>₱{formatNumber(text)}</span>,
    },
    {
      title: "Branch",
      dataIndex: "branch_name",
      key: "branch_name",
    },
    {
      title: "Purpose",
      dataIndex: "purpose",
      key: "purpose",
    },
    {
      title: "Added By",
      dataIndex: "added_by_name",
      key: "added_by_name",
    },
    {
      title: "Date",
      dataIndex: "added_on",
      key: "added_on",
      render: (date: any) => formatDate(date, "MMM DD, YYYY h:mm A"),
    },
  ];

  async function exportToExcel() {
    setLoading("export");
    try {
      const exportFilter = { ...filterConfig, page: "" };
      const response = await getRequest(`borrowed_cashes/get`, exportFilter, true);
      if (response.success) {
        handleExportToExcel(response.data.data, columns, "BorrowedCash");
      } else {
        message.error("Failed to fetch data for export.");
      }
    } catch {
      message.error("Error exporting data.");
    } finally {
      setLoading("");
    }
  }

  async function exportToPDF() {
    setLoading("export");
    try {
      const exportFilter = { ...filterConfig, page: "", page_size: "" };
      const response = await getRequest(`borrowed_cashes/get`, exportFilter, true);
      if (response.success) {
        const exportReadyData = response.data.data;
        handleExportToPDF(
          exportReadyData,
          columns,
          "BorrowedCash.pdf",
          {
            title: "List of Borrowed Cash",
            content: `Generated on ${new Date().toLocaleString()}`,
          },
          `Total Records: ${exportReadyData.length}`
        );
      } else {
        message.error("Failed to fetch data for PDF export.");
      }
    } catch {
      message.error("Error exporting PDF.");
    } finally {
      setLoading("");
    }
  }

  async function submitForm(data: any) {
    setLoading("submit");
    const response = await postRequest(
      `borrowed_cashes/${showModal}`,
      { borrowed_cash_id: selectedRow.id, ...data },
      true
    );
    if (response.success) {
      setSuccessMessage(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setShowModal("");
        setLoading("");
        setSuccessMessage("");
        fetchTableData();
      }, 1000);
    } else {
      setLoading("");
      setErrorMessage(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${showModal} borrowed cash.`
      );
    }
  }

  async function deleteBorrowedCash() {
    const response = await postRequest(
      `borrowed_cashes/delete`,
      { borrowed_cash_id: selectedRow.id },
      true
    );
    if (response.success) {
      setSuccessMessage(formatString(response.data.message));
      setTimeout(() => {
        setShowModal("");
        clearFields();
        setSuccessMessage("");
        fetchTableData();
      }, 1000);
    } else {
      setErrorMessage(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to delete borrowed cash."
      );
    }
  }

  async function fetchTableData() {
    setDataSource([]);
    setLoading("fetch");
    const response = await getRequest(`borrowed_cashes/get`, filterConfig, true);
    if (response.success) {
      setDataSize(response.data.size);
      setDataSource(response.data.data);
    }
    setLoading("");
  }

  async function fetchBranches() {
    setBranches([]);
    const response = await getRequest(`branches/get`, true);
    if (response.success) {
      setBranches(response.data.data);
    }
  }

  useEffect(() => {
    fetchTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfig]);

  useEffect(() => {
    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Borrowed Cash"
              withDateRangeFilter={false}
              withAddButton={true}
              addBtnText="Borrowed Cash"
              onCreate={() => setShowModal("create")}
              withExportDropdownButton={true}
              disableExportButton={dataSource?.length === 0}
              onExportToExcel={() => exportToExcel()}
              onExportToPDF={() => exportToPDF()}
              withSearchBtn={true}
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({ ...prev, search: value }))
              }
            />
          </Col>

          {/* Filter */}
          <Col xs={24} md={12} lg={8} className="mb-1">
            <span><strong>Filter By:</strong></span>
          </Col>
          <Col span={24} className="mb-3">
            <Row>
              <Col xs={24} md={12} lg={5} className="mb-2 me-2 mb-lg-0">
                <Select
                  className="w-full"
                  showSearch
                  placeholder="Select branch"
                  value={filterConfig.branch_id || ""}
                  onChange={(value) => handleFilterChange(value, "branch_id")}
                  allowClear
                  filterOption={(input: string, option?: any) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  <Option value="">All Branches</Option>
                  {branches.map((branch: any) => (
                    <Option key={branch.id} value={branch.id}>
                      {branch.name}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="min-w-[600px]">
                <CustomTable
                  data={dataSource}
                  columns={columns}
                  rowKey="id"
                  loading={loading === "fetch"}
                  size="small"
                  pagination={{
                    current: filterConfig.page,
                    total: dataSize,
                    showSizeChanger: true,
                    onChange: (page: number) =>
                      handleChangePage(page, setFilterConfig),
                    onShowSizeChange: (_: number, size: number) =>
                      handlePageSizeChange(size, setFilterConfig),
                  }}
                  handleRowClick={(record: any) => {
                    setShowModal("update");
                    setSelectedRow(record);
                    form.setFieldsValue({ ...record });
                  }}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Layout>

      {/* Create / Update Modal */}
      <Modal
        open={showModal === "create" || showModal === "update"}
        title={`${showModal === "create" ? "Add" : "Edit"} Borrowed Cash`}
        centered
        footer={null}
        onCancel={() => {
          clearFields();
          setShowModal("");
        }}
        width={600}
        maskClosable={false}
      >
        <div className="max-h-[70vh] overflow-y-auto pr-2">
          <Form form={form} onFinish={submitForm} layout="vertical">
            <Row gutter={[10, 10]} className="mt-2">

              {/* Amount */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Amount"
                  name="amount"
                  rules={[{ required: true, message: "Amount is required." }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    precision={2}
                    placeholder="0.00"
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) => value?.replace(/,/g, "") as any}
                  />
                </Form.Item>
              </Col>

              {/* Branch */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Branch"
                  name="branch_id"
                  rules={[{ required: true, message: "Branch is required." }]}
                  initialValue={getUserRole() === "cashier" ? getUserBranch() : undefined}
                >
                  <Select
                    placeholder="Select branch"
                    showSearch
                    allowClear
                    disabled={getUserRole() === "cashier"}
                    options={branches.map((b: any) => ({ value: b.id, label: b.name }))}
                    filterOption={(input, option) =>
                      typeof option?.label === "string" &&
                      option.label.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>

              {/* Purpose */}
              <Col xs={24}>
                <Form.Item
                  label="Purpose"
                  name="purpose"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: "Purpose is required.",
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={3}
                    placeholder="Enter purpose of borrowed cash..."
                  />
                </Form.Item>
              </Col>

              {/* Actions */}
              <Col xs={24} className="flex justify-end gap-3 mt-2">
                <Button
                  onClick={() => {
                    clearFields();
                    setShowModal("");
                  }}
                >
                  Cancel
                </Button>
                {showModal === "update" && (
                  <Popconfirm
                    title="Are you sure you want to delete this record?"
                    onConfirm={() => deleteBorrowedCash()}
                    okText="Yes"
                  >
                    <Button danger>Delete</Button>
                  </Popconfirm>
                )}
                <Button
                  htmlType="submit"
                  type="primary"
                  loading={loading === "submit"}
                >
                  Submit
                </Button>
              </Col>

            </Row>
          </Form>
        </div>
      </Modal>

      {/* Error Modal */}
      <ErrorModal
        showModal={!!errorMessage}
        onCancel={() => setErrorMessage("")}
        title="Action Failed"
        body={errorMessage}
      />

      {/* Success Modal */}
      <SuccessModal
        showModal={!!successMessage}
        onCancel={() => setSuccessMessage("")}
        title="Success"
        body={successMessage}
      />

      <PersistentFooter />
    </Layout>
  );
};

export default BorrowedCash;